import { useState } from 'react';
import {
  Button,
  Checkbox,
  Icon,
  Input,
  Modal,
  Stack,
  icons,
  theme,
} from '@agentisai/design-system';
import type { EvalDocument } from '@agentisai/shared-types';
import { formatBytes, muted } from './format';

/**
 * "Attach documents" dialog: search + a scrolling checklist, so a bucket of
 * 20 or 200 files stays one short list away. Works on a local copy of the
 * selection (R1.0) and hands it back only on Done — Cancel changes nothing.
 */
export function DocumentPicker({
  documents,
  selected,
  onDone,
  onClose,
}: {
  documents: EvalDocument[];
  selected: string[];
  onDone: (next: string[]) => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState(selected);
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? documents.filter((d) => d.name.toLowerCase().includes(q)) : documents;
  const toggle = (name: string, on: boolean) =>
    setPicked(on ? [...picked, name] : picked.filter((p) => p !== name));

  return (
    <Modal
      open
      onClose={onClose}
      title="Attach documents"
      size="md"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onDone(picked)}>
            {picked.length === 0 ? 'Attach none' : `Attach ${picked.length}`}
          </Button>
        </>
      }
    >
      <Stack direction="column" gap={3}>
        <Input
          label="Search documents"
          leftIcon={<Icon as={icons.Search} size="sm" />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. policy"
          fullWidth
        />
        <span style={muted} aria-live="polite">
          {documents.length === 0
            ? 'No documents uploaded yet. Upload them in the Documents tab.'
            : `${shown.length} of ${documents.length} shown · ${picked.length} selected`}
        </span>
        <Stack
          direction="column"
          gap={2}
          style={{ maxHeight: '50vh', overflowY: 'auto', paddingBlock: theme.space[1] }}
        >
          {shown.map((doc) => (
            <Checkbox
              key={doc.name}
              label={doc.name}
              helper={formatBytes(doc.sizeBytes)}
              checked={picked.includes(doc.name)}
              onChange={(e) => toggle(doc.name, e.target.checked)}
            />
          ))}
        </Stack>
      </Stack>
    </Modal>
  );
}
DocumentPicker.displayName = 'DocumentPicker';
